/**
 * Registro de actividad: qué pantallas abre cada usuario, con qué filtros lista, qué
 * reportes mira. Por ahora solo se guarda (POST /api/activity, activityController),
 * para ver después si la IA puede proponerle a cada uno lo que suele hacer.
 *
 * Liviano a propósito: los eventos se juntan en memoria y salen todos juntos cada
 * minuto, y al ocultar o cerrar la pestaña. Si el tenant lo apagó en Ajustes, el back
 * responde enabled: false y no se manda nada más hasta la próxima carga. Si falla, no
 * pasa nada: nadie se entera.
 */
import { getWencesApiBaseUrl } from "@/api/wencesApi";

const FLUSH_MS = 60 * 1000;
const MAX_BUFFER = 200;
const MAX_VALUE_CHARS = 120;

let buffer = [];
let disabled = false;
let started = false;
let lastListKey = "";

/** Valores cortos: un filtro con un texto larguísimo no aporta nada para aprender hábitos. */
function shorten(obj) {
  if (!obj || typeof obj !== "object") return undefined;
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v == null || v === "") return;
    const s = Array.isArray(v) ? v.map(String).join(",") : typeof v === "object" ? JSON.stringify(v) : String(v);
    out[k] = s.length > MAX_VALUE_CHARS ? `${s.slice(0, MAX_VALUE_CHARS)}…` : s;
  });
  return Object.keys(out).length ? out : undefined;
}

export function trackActivity(type, { path, route, model, itemId, data } = {}) {
  if (disabled) return;
  try {
    if (!localStorage.getItem("token")) return;
  } catch (e) {
    return;
  }
  if (buffer.length >= MAX_BUFFER) buffer.shift();
  buffer.push({
    at: new Date().toISOString(),
    type,
    path: path || (window.location && window.location.pathname) || "",
    route: route || "",
    model: model || "",
    itemId: itemId || "",
    data: shorten(data),
  });
}

/**
 * Un listado con sus filtros. El listado vuelve a pedir datos por muchos motivos
 * (recargas, volver a la pestaña): se registra solo si cambió algo.
 */
export function trackListActivity(model, params) {
  const data = {};
  params.forEach((value, key) => {
    if (key === "fields" || key === "_") return;
    data[key] = value;
  });
  const key = `${model}|${JSON.stringify(data)}`;
  if (key === lastListKey) return;
  lastListKey = key;
  trackActivity("list", { model, data });
}

async function flush(useKeepalive) {
  if (disabled || !buffer.length) return;
  let token = "";
  try {
    token = localStorage.getItem("token") || "";
  } catch (e) {
    return;
  }
  if (!token) {
    buffer = [];
    return;
  }
  const events = buffer;
  buffer = [];
  try {
    const res = await fetch(`${getWencesApiBaseUrl()}/api/activity`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ events }),
      // Al cerrar la pestaña el pedido sigue aunque la página ya no exista.
      keepalive: !!useKeepalive,
    });
    if (useKeepalive) return;
    const body = await res.json().catch(() => null);
    if (body && body.enabled === false) {
      disabled = true;
      buffer = [];
    }
  } catch (e) {
    // Sin red o el back caído: esos eventos se pierden y listo.
  }
}

/** Una vez al arrancar la app: cada cambio de pantalla es un evento. */
export function startActivityTracker(router) {
  if (started) return;
  started = true;
  router.afterEach((to) => {
    const p = to.params || {};
    trackActivity("view", {
      path: to.path,
      route: to.name ? String(to.name) : "",
      model: p.modelName ? String(p.modelName) : "",
      itemId: p.itemId ? String(p.itemId) : "",
      data: Object.keys(to.query || {}).length ? to.query : undefined,
    });
  });
  setInterval(() => flush(false), FLUSH_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
}
